// Favoris - Annonces sauvegardées
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart, Package, MapPin, Clock, Trash2, Eye, MessageCircle,
  Bell, BellOff, Share2, ExternalLink, Search, ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../../hooks/useCurrentUser';

function FavoriCard({ favori, onRemove, onToggleAlert }) {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="flex">
        {/* Image */}
        <div className="w-32 sm:w-40 h-32 bg-gray-100 relative flex-shrink-0">
          {favori.image ? (
            <img src={favori.image} alt={favori.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={32} className="text-gray-300" />
            </div>
          )}
          {favori.vendu && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white font-bold text-sm">VENDU</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col">
          <div className="flex-1">
            <h3 
              className="font-semibold text-gray-900 line-clamp-1 cursor-pointer hover:text-orange-600"
              onClick={() => navigate(`/annonce/${favori.id}`)}
            >
              {favori.title}
            </h3>
            <p className="text-lg font-bold text-orange-600 mt-1">{favori.price} €</p>
            
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin size={12} />
                {favori.ville}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {favori.date}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/annonce/${favori.id}`)}
              className="flex-1 sm:flex-none"
            >
              <Eye size={14} className="mr-1" /> Voir
            </Button>
            <Button
              size="sm"
              className="flex-1 sm:flex-none bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => navigate(`/messages?annonce=${favori.id}`)}
            >
              <MessageCircle size={14} className="mr-1" /> Contacter
            </Button>
            <button
              onClick={() => onToggleAlert(favori.id)}
              className={`p-2 rounded-lg transition-colors ${
                favori.alerte 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'hover:bg-gray-100 text-gray-400'
              }`}
              title={favori.alerte ? 'Alerte activée' : 'Activer l\'alerte'}
            >
              {favori.alerte ? <Bell size={16} /> : <BellOff size={16} />}
            </button>
            <button
              onClick={() => onRemove(favori.id)}
              className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
              title="Retirer des favoris"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function FavorisPage({ onSelectTool }) {
  const { user } = useCurrentUser();
  const [favoris, setFavoris] = useState([]);
  const [loading, setLoading] = useState(true);

  // Données démo
  const DEMO_FAVORIS = [];

  useEffect(() => {
    setTimeout(() => {
      if (user?.isDemo) {
        setFavoris(DEMO_FAVORIS);
      } else {
        setFavoris([]);
      }
      setLoading(false);
    }, 300);
  }, [user]);

  const handleRemove = (id) => {
    setFavoris(prev => prev.filter(f => f.id !== id));
  };

  const handleToggleAlert = (id) => {
    setFavoris(prev => prev.map(f => 
      f.id === id ? { ...f, alerte: !f.alerte } : f
    ));
  };

  const activeCount = favoris.filter(f => !f.vendu).length;
  const alertCount = favoris.filter(f => f.alerte).length;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onSelectTool && (
          <button 
            onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Retour au tableau de bord"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Mes Favoris</h1>
          <p className="text-gray-500">
            {favoris.length} annonce{favoris.length > 1 ? 's' : ''} sauvegardée{favoris.length > 1 ? 's' : ''}
            {alertCount > 0 && <span className="text-blue-600"> • {alertCount} alerte{alertCount > 1 ? 's' : ''} active{alertCount > 1 ? 's' : ''}</span>}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Heart size={20} className="text-red-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{favoris.length}</p>
              <p className="text-xs text-gray-500">Total favoris</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Package size={20} className="text-green-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{activeCount}</p>
              <p className="text-xs text-gray-500">Disponibles</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Bell size={20} className="text-blue-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{alertCount}</p>
              <p className="text-xs text-gray-500">Alertes actives</p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : favoris.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Heart size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun favori</h3>
          <p className="text-gray-500 mb-4">
            Sauvegarde des annonces pour les retrouver facilement
          </p>
          <Link to="/particuliers">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              Parcourir les annonces
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {favoris.map((favori) => (
            <FavoriCard
              key={favori.id}
              favori={favori}
              onRemove={handleRemove}
              onToggleAlert={handleToggleAlert}
            />
          ))}
        </div>
      )}
    </div>
  );
}
